/**
 * County Detail Page
 * Dynamic route for county-specific waste management information
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CountyHero } from '../../components/county/CountyHero';
import { CountyRequirements } from '../../components/county/CountyRequirements';
import { FacilityDirectory } from '../../components/county/FacilityDirectory';
import { ServiceProviderCard } from '../../components/county/ServiceProviderCard';
import { CountyFAQ } from '../../components/county/CountyFAQ';
import type { County, Facility, ServiceProvider } from '../../types/county';

// API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

/**
 * API response types
 */
interface ApiCounty {
  id: string;
  slug: string;
  name: string;
  region?: string;
  population?: number;
  facilityCount?: number;
  green_halo_required?: boolean;
  diversion_rate_min?: number;
  ordinance_summary?: string;
  ordinance_url?: string;
  description?: string;
  lea_name?: string;
  lea_phone?: string;
  lea_email?: string;
  lea_website?: string;
  county_seat?: string;
}

interface ApiFacility {
  id: string;
  name: string;
  city: string;
  address: string;
  state: string;
  zip_code: string;
  contact_phone?: string;
  contact_email?: string;
  accepted_materials?: string[];
}

interface ApiServiceProvider {
  id: string;
  name: string;
  service_types?: string[];
  service_area?: string[];
  contact_phone?: string;
  contact_email?: string;
  website?: string;
  notes?: string;
  verified?: boolean;
}

/**
 * Transform backend county data to frontend County type
 */
function transformCountyData(apiCounty: ApiCounty): County {
  return {
    id: apiCounty.id,
    slug: apiCounty.slug,
    name: apiCounty.name,
    region: apiCounty.region || 'California',
    population: apiCounty.population || 0,
    diversionRate: 65, // Default or calculate from actual data
    facilityCount: apiCounty.facilityCount || 0,
    greenHaloRequired: Boolean(apiCounty.green_halo_required),
    requirements: {
      diversionRate: apiCounty.diversion_rate_min || 65,
      greenHaloThreshold: apiCounty.green_halo_required ? 100 : undefined,
      localOrdinances: apiCounty.ordinance_summary
        ? [
            {
              title: 'Local Ordinance',
              description: apiCounty.ordinance_summary,
              link: apiCounty.ordinance_url || undefined,
              effectiveDate: undefined,
            },
          ]
        : [],
      additionalNotes: apiCounty.description || undefined,
    },
    contactInfo: {
      department: apiCounty.lea_name || `${apiCounty.name} County Building Department`,
      phone: apiCounty.lea_phone || undefined,
      email: apiCounty.lea_email || undefined,
      website: apiCounty.lea_website || undefined,
      address: apiCounty.county_seat
        ? `${apiCounty.county_seat}, CA`
        : undefined,
    },
  };
}

/**
 * Transform backend facility data to frontend Facility type
 */
function transformFacilityData(apiFacility: ApiFacility): Facility {
  return {
    id: apiFacility.id,
    name: apiFacility.name,
    city: apiFacility.city,
    address: `${apiFacility.address}, ${apiFacility.city}, ${apiFacility.state} ${apiFacility.zip_code}`,
    phone: apiFacility.contact_phone || undefined,
    email: apiFacility.contact_email || undefined,
    website: undefined,
    materialsAccepted: Array.isArray(apiFacility.accepted_materials)
      ? apiFacility.accepted_materials
      : [],
    countyApproved: true,
    hours: undefined,
    notes: undefined,
  };
}

/**
 * Transform backend service provider data to frontend ServiceProvider type
 */
function transformServiceProviderData(apiProvider: ApiServiceProvider): ServiceProvider {
  return {
    id: apiProvider.id,
    name: apiProvider.name,
    services: Array.isArray(apiProvider.service_types)
      ? apiProvider.service_types
      : [],
    serviceArea: Array.isArray(apiProvider.service_area)
      ? apiProvider.service_area
      : [],
    phone: apiProvider.contact_phone || undefined,
    email: apiProvider.contact_email || undefined,
    website: apiProvider.website || undefined,
    description: apiProvider.notes || undefined,
    countyApproved: Boolean(apiProvider.verified),
  };
}

// Mock data - Commented out until needed for development
// Prefixed with underscore to indicate intentionally unused
// @ts-expect-error - Keeping for future reference
const _MOCK_COUNTY: County = {
  id: 'alameda',
  slug: 'alameda',
  name: 'Alameda',
  region: 'Bay Area',
  population: 1671329,
  diversionRate: 72,
  facilityCount: 28,
  greenHaloRequired: true,
  requirements: {
    diversionRate: 65,
    greenHaloThreshold: 100,
    localOrdinances: [
      {
        title: 'Construction & Demolition Debris Ordinance',
        description:
          'All construction and demolition projects must divert at least 65% of waste from landfills. Projects generating over 100 tons require Green Halo certification.',
        link: 'https://example.com/ordinance',
        effectiveDate: '2020-01-01',
      },
    ],
    additionalNotes:
      'Projects in unincorporated areas must also comply with county-specific requirements. Contact the Planning Department for details.',
  },
  contactInfo: {
    department: 'Alameda County Building Inspection Department',
    phone: '(510) 670-5400',
    email: 'building@acgov.org',
    website: 'https://www.acgov.org/cda/building',
    address: '224 W. Winton Ave, Hayward, CA 94544',
  },
};

// @ts-expect-error - Keeping for future reference
const _MOCK_FACILITIES: Facility[] = [
  {
    id: 'f1',
    name: 'Davis Street Transfer Station',
    city: 'San Leandro',
    address: '2615 Davis Street, San Leandro, CA 94577',
    phone: '(510) 613-8710',
    website: 'https://www.stopwaste.org',
    materialsAccepted: ['Concrete', 'Wood', 'Metal', 'Cardboard', 'Drywall'],
    countyApproved: true,
    distance: 5.2,
    hours: 'Mon-Fri: 7am-4pm, Sat: 8am-4pm',
  },
  {
    id: 'f2',
    name: 'Waste Management - Oakland',
    city: 'Oakland',
    address: '2100 Oakport Street, Oakland, CA 94621',
    phone: '(510) 613-8710',
    materialsAccepted: ['Mixed C&D', 'Wood', 'Metal', 'Concrete'],
    countyApproved: true,
    distance: 8.5,
  },
];

// @ts-expect-error - Keeping for future reference
const _MOCK_PROVIDERS: ServiceProvider[] = [
  {
    id: 'p1',
    name: 'Waste Management - Alameda County',
    services: ['Roll-off Dumpsters', 'Mixed C&D Sorting', 'Recycling Services'],
    serviceArea: ['Alameda County', 'Contra Costa County'],
    phone: '(510) 613-8710',
    website: 'https://www.wm.com',
    description:
      'Full-service waste management and recycling for construction projects of all sizes.',
    countyApproved: true,
  },
  {
    id: 'p2',
    name: 'Republic Services',
    services: ['Container Service', 'Recycling', 'Waste Audits'],
    serviceArea: ['Alameda County'],
    phone: '(510) 000-0000',
    website: 'https://www.republicservices.com',
    description: 'Comprehensive waste solutions with dedicated construction division.',
    countyApproved: true,
  },
];

const MOCK_FAQS = [
  {
    question: 'Do I need a Green Halo certificate for my project?',
    answer:
      'Green Halo certification is required for projects generating over 100 tons of waste. Check with your building department to confirm requirements for your specific project.',
    category: 'compliance',
  },
  {
    question: 'Where can I get weight tickets?',
    answer:
      'All approved facilities provide weight tickets automatically. Make sure to request them at drop-off and keep copies for your records.',
    category: 'documentation',
  },
  {
    question: 'Can I use facilities outside the county?',
    answer:
      'Yes, but verify that out-of-county facilities are approved by checking the CalRecycle database. Some jurisdictions have specific requirements.',
    category: 'facilities',
  },
];

export function CountyPage() {
  const { slug } = useParams<{ slug: string }>();
  const [county, setCounty] = useState<County | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [providers, setProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadCountyData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch county data
        const countyResponse = await fetch(`${API_BASE_URL}/api/v1/counties/${slug}`);
        if (!countyResponse.ok) {
          if (countyResponse.status === 404) {
            throw new Error('County not found');
          }
          throw new Error(`Failed to fetch county: ${countyResponse.statusText}`);
        }
        const countyData = await countyResponse.json();

        // Fetch facilities for this county
        const facilitiesResponse = await fetch(
          `${API_BASE_URL}/api/v1/counties/${slug}/facilities`
        );
        if (!facilitiesResponse.ok) {
          throw new Error(`Failed to fetch facilities: ${facilitiesResponse.statusText}`);
        }
        const facilitiesData = await facilitiesResponse.json();

        // Fetch service providers for this county
        const providersResponse = await fetch(
          `${API_BASE_URL}/api/v1/counties/${slug}/providers`
        );
        if (!providersResponse.ok) {
          throw new Error(`Failed to fetch providers: ${providersResponse.statusText}`);
        }
        const providersData = await providersResponse.json();

        // Transform API data to frontend types
        setCounty(transformCountyData(countyData.county));
        setFacilities(facilitiesData.facilities.map(transformFacilityData));
        setProviders(providersData.providers.map(transformServiceProviderData));
      } catch (err) {
        console.error('Error loading county data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load county data');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadCountyData();
    }
  }, [slug]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-neutral-600">Loading county information...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !county) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">🔍</div>
          <h1 className="text-3xl font-bold text-neutral-900 mb-3">County Not Found</h1>
          <p className="text-neutral-600 mb-6">
            {error || `We couldn't find information for "${slug}" county.`}
          </p>
          <Link
            to="/counties"
            className="inline-block bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
          >
            View All Counties
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <CountyHero county={county} />

      {/* Requirements Section */}
      <CountyRequirements requirements={county.requirements} countyName={county.name} />

      {/* Facility Directory */}
      <FacilityDirectory facilities={facilities} countyName={county.name} />

      {/* Service Providers */}
      <section id="providers" className="py-12 border-b border-neutral-200">
        <div className="container mx-auto px-4 max-w-6xl">
          <h2 className="text-3xl font-bold text-neutral-900 mb-3">
            Service Providers
          </h2>
          <p className="text-neutral-600 mb-6">
            Approved waste management service providers serving {county.name} County
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {providers.map(provider => (
              <ServiceProviderCard key={provider.id} provider={provider} />
            ))}
          </div>

          {providers.length === 0 && (
            <div className="bg-neutral-50 rounded-lg p-8 text-center text-neutral-500">
              No service providers currently listed. Check back soon or contact your local building department.
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <CountyFAQ faqs={MOCK_FAQS} countyName={county.name} />

      {/* Contact Section */}
      <section className="py-12 bg-neutral-50">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl font-bold text-neutral-900 mb-6">
            Contact Building Department
          </h2>
          <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-6">
            <h3 className="font-semibold text-lg text-neutral-900 mb-4">
              {county.contactInfo.department}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {county.contactInfo.phone && (
                <div>
                  <div className="text-neutral-500 mb-1">Phone</div>
                  <a
                    href={`tel:${county.contactInfo.phone}`}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {county.contactInfo.phone}
                  </a>
                </div>
              )}
              {county.contactInfo.email && (
                <div>
                  <div className="text-neutral-500 mb-1">Email</div>
                  <a
                    href={`mailto:${county.contactInfo.email}`}
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    {county.contactInfo.email}
                  </a>
                </div>
              )}
              {county.contactInfo.website && (
                <div>
                  <div className="text-neutral-500 mb-1">Website</div>
                  <a
                    href={county.contactInfo.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Visit Website →
                  </a>
                </div>
              )}
              {county.contactInfo.address && (
                <div>
                  <div className="text-neutral-500 mb-1">Address</div>
                  <div className="text-neutral-700">{county.contactInfo.address}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-primary-600">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Create Your Waste Management Plan?
          </h2>
          <p className="text-primary-100 mb-8 text-lg">
            Get started with a compliant plan for {county.name} County in minutes
          </p>
          <Link
            to="/plans/create"
            className="inline-block bg-white text-primary-600 px-8 py-4 rounded-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg"
          >
            Create Plan Now
          </Link>
        </div>
      </section>
    </div>
  );
}
